import { useState, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Modal, Form } from 'react-bootstrap';
import {
  RouteAnalysisResponse,
  FuelIntervalResponse,
  StrategyOptions,
} from '../../models';
import { calculateFuelIntervals } from '../../api/Api';
import FuelSummaryCard from './FuelSummaryCard';
import RouteEnergyChart from './RouteEnergyChart';
import PlannerNavigation from './PlannerNavigation';
import FuelPointCard from './FuelPointCard';

interface Props {
  route: RouteAnalysisResponse;
  strategyOptions: StrategyOptions;
  intervals: FuelIntervalResponse[];
  totalDuration: string;
  onComplete: (intervals: FuelIntervalResponse[], totalDuration: string) => void;
  onBack: () => void;
}

export type EditableInterval = FuelIntervalResponse & { id: number };

let nextId = 0;
function toEditable(i: FuelIntervalResponse): EditableInterval {
  return { ...i, id: nextId++ };
}

export function formatDuration(iso: string): string {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return iso;
  const h = parseInt(match[1] ?? '0');
  const m = parseInt(match[2] ?? '0');
  return `${h}:${String(m).padStart(2, '0')}`;
}

export default function FuelIntervalEditor({ route, strategyOptions, intervals: initialIntervals, totalDuration, onComplete, onBack }: Props) {
  const [editableIntervals, setEditableIntervals] = useState<EditableInterval[]>(
    () => initialIntervals.map(toEditable)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [lastBurnedKcal, setLastBurnedKcal] = useState(
    () => initialIntervals.reduce((s, i) => s + i.burnedKcal, 0)
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKm, setNewKm] = useState<string>('');

  const recalculate = useCallback(async (kmPositions: number[]) => {
    setLoading(true);
    setError(null);
    try {
      const result = await calculateFuelIntervals({
        route,
        cyclist: strategyOptions.cyclist,
        targetZone: strategyOptions.targetZone,
        frequencyMinutes: strategyOptions.frequencyMinutes,
        maxAbsorptionRatePerHour: strategyOptions.maxAbsorptionRatePerHour,
        maxSurplusGramsPerInterval: strategyOptions.maxSurplusGramsPerInterval,
        kmPositions,
      });
      setEditableIntervals(result.intervals.map(toEditable));
      setLastBurnedKcal(result.intervals.reduce((s, i) => s + i.burnedKcal, 0));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to recalculate intervals');
    } finally {
      setLoading(false);
    }
  }, [route, strategyOptions]);

  const handleChartClick = useCallback((data: any) => {
    if (data?.activeLabel === undefined || loading) return;
    const km = parseFloat(Number(data.activeLabel).toFixed(1));
    if (isNaN(km) || km <= 0 || km >= route.totalDistanceKm) return;
    if (editableIntervals.some(i => Math.abs(i.kilometerMark - km) < 0.5)) return;
    const newInterval = toEditable({ kilometerMark: km, targetCarbs: 0, estimatedTime: 'PT0S', burnedKcal: 0 });
    const next = [...editableIntervals, newInterval].sort((a, b) => a.kilometerMark - b.kilometerMark);
    setEditableIntervals(next);
  }, [editableIntervals, route.totalDistanceKm, loading]);

  const handleRecalculate = useCallback(() => {
    recalculate(editableIntervals.map(i => i.kilometerMark));
  }, [editableIntervals, recalculate]);

  const handleKmBlur = useCallback((id: number, value: number) => {
    if (isNaN(value) || value <= 0 || value >= route.totalDistanceKm) return;
    const next = editableIntervals.map(i => i.id === id ? { ...i, kilometerMark: value } : i);
    setEditableIntervals(next);
  }, [editableIntervals, route.totalDistanceKm]);

  const handleCarbsChange = useCallback((id: number, value: number) => {
    const clamped = Math.max(0, Math.min(1000, value));
    setEditableIntervals(prev => prev.map(i => i.id === id ? { ...i, targetCarbs: clamped } : i));
  }, []);

  const handleDelete = useCallback((id: number) => {
    const next = editableIntervals.filter(i => i.id !== id);
    setEditableIntervals(next);
  }, [editableIntervals]);

  const handleAddFuelPoint = useCallback(() => {
    const km = parseFloat(newKm);
    if (isNaN(km) || km <= 0 || km >= route.totalDistanceKm) return;
    if (editableIntervals.some(i => Math.abs(i.kilometerMark - km) < 0.5)) return;
    const newInterval = toEditable({ kilometerMark: km, targetCarbs: 0, estimatedTime: 'PT0S', burnedKcal: 0 });
    const next = [...editableIntervals, newInterval].sort((a, b) => a.kilometerMark - b.kilometerMark);
    setEditableIntervals(next);
    setShowAddModal(false);
    setNewKm('');
  }, [newKm, editableIntervals, route.totalDistanceKm]);

  const handleConfirm = useCallback(() => {
    const plain: FuelIntervalResponse[] = editableIntervals.map(({ id: _id, ...i }) => i);
    onComplete(plain, totalDuration);
  }, [editableIntervals, totalDuration, onComplete]);

  const totalCarbs = editableIntervals.reduce((s, i) => s + i.targetCarbs, 0);
  const totalConsumedKcal = totalCarbs * 4;
  const totalBurnedKcal = lastBurnedKcal;
  const highlightedKm = hoveredId != null
    ? (editableIntervals.find(i => i.id === hoveredId)?.kilometerMark ?? null)
    : null;
  const fuelPoints = editableIntervals.map(i => ({ km: i.kilometerMark, carbs: i.targetCarbs }));

  return (
    <div className="d-flex flex-column gap-3">
      <FuelSummaryCard
        totalCarbs={totalCarbs}
        totalConsumedKcal={totalConsumedKcal}
        totalBurnedKcal={totalBurnedKcal}
        totalDuration={totalDuration}
      />

      <div className="d-md-none mb-2">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
          className="w-100"
        >
          + Dodaj punkt
        </Button>
      </div>

      <Card className="sticky-header">
        <Card.Body>
          <RouteEnergyChart
            route={route}
            strategyOptions={strategyOptions}
            fuelPoints={fuelPoints}
            height={240}
            cursor={loading ? 'wait' : 'crosshair'}
            onClick={handleChartClick}
            highlightedKm={highlightedKm}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Fuel Points ({editableIntervals.length})</Card.Title>
          <div className="d-flex align-items-center gap-2">
            {loading && <Spinner size="sm" />}
            <Button size="sm" variant="outline-secondary" onClick={handleRecalculate} disabled={loading || editableIntervals.length === 0}>
              Recalculate
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {editableIntervals.length === 0 ? (
            <p className="text-secondary mb-0">No fuel points. Click on the chart to add one.</p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {editableIntervals.map(interval => (
                <FuelPointCard
                  key={`${interval.id}-${interval.kilometerMark}`}
                  interval={interval}
                  onDelete={handleDelete}
                  onCarbsChange={handleCarbsChange}
                  onKmBlur={handleKmBlur}
                  onHover={setHoveredId}
                />
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Dodaj punkt tankowania</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>km</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              min="0"
              max={route.totalDistanceKm}
              placeholder="0"
              value={newKm}
              onChange={(e) => setNewKm(e.target.value)}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Anuluj
          </Button>
          <Button variant="primary" onClick={handleAddFuelPoint} disabled={!newKm}>
            Dodaj
          </Button>
        </Modal.Footer>
      </Modal>

      <PlannerNavigation
        onBack={onBack}
        onNext={handleConfirm}
        loading={loading}
        disabled={editableIntervals.length === 0}
      />
    </div>
  );
}
